import { useCallback, useEffect, useState } from 'react';

type Props = {
  startDate?: string | Date | null;
  endDate?: string | Date | null;
};

const formatTime = (durationMs: number): string => {
  const totalSeconds = Math.floor(durationMs / 1000);
  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
    2,
    '0',
  );
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
};

const getUtcTimestampWithMicroseconds = (): string => {
  const now = new Date();

  const pad = (n: number, width = 2) => String(n).padStart(width, '0');

  const datePart = `${now.getUTCFullYear()}-${pad(now.getUTCMonth() + 1)}-${pad(
    now.getUTCDate(),
  )}`;
  const timePart = `${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}:${pad(
    now.getUTCSeconds(),
  )}`;
  const milliseconds = now.getUTCMilliseconds();

  const microseconds = `${String(milliseconds).padStart(3, '0')}000`;

  return `${datePart}T${timePart}.${microseconds}`;
};

function TimeElapsed({ startDate, endDate }: Props) {
  const getElapsed = useCallback(() => {
    if (!startDate) return 0;
    const start = new Date(new Date(startDate).toUTCString()).getTime();
    const endUtc = endDate ?? getUtcTimestampWithMicroseconds();
    const end = new Date(new Date(endUtc).toUTCString()).getTime();
    return Math.max(end - start, 0);
  }, [endDate, startDate]);

  const [elapsed, setElapsed] = useState(getElapsed());

  useEffect(() => {
    if (!startDate || endDate) {
      setElapsed(getElapsed());
      return;
    }

    const interval = setInterval(() => {
      setElapsed(getElapsed());
    }, 1000);

    // eslint-disable-next-line consistent-return
    return () => {
      clearInterval(interval);
    };
  }, [startDate, endDate, getElapsed]);

  return <span>{startDate ? formatTime(elapsed) : '--:--:--'}</span>;
}

export default TimeElapsed;
